<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import WhatsappSessionsAPI from 'dashboard/api/whatsappSessions';
import InboxMembersAPI from 'dashboard/api/inboxMembers';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { accountId } = useAccount();
const agents = useMapGetter('agents/getAgents');

const POLL_INTERVAL = 3000;
const MAX_CONSECUTIVE_FAILURES = 5;
const LIMIT_CHECK_TIMEOUT_MS = 4000;
const CANCEL_LOCK_MS = 600;
const CANCEL_CONFIRM_TIMEOUT_MS = 5000;
// Quanto tempo em `preparando` antes de avisar que está demorando mais que
// o normal (serviço degradado) em vez de deixar a pessoa esperando calada.
const PREPARING_SLOW_MS = 60000;
// Estados em que a caixa já tem um destino final e o polling deve parar.
// `falhou` e `sem_proxy` ficam de fora: o broker/daemon tentam se recuperar
// sozinhos (troca de proxy, novo provisionamento) e a tela deve continuar
// acompanhando em vez de desistir.
const TERMINAL_STATES = [
  'conectado',
  'numero_diferente',
  'pausado',
  'expirado',
];

const step = ref('form'); // form | tracking
const formReady = ref(false);
const limitReached = ref(false);
const name = ref('');
// Pré-preenchido com o DDI do Brasil: sem isso, um número digitado sem o 55
// passa na validação (que aceita `\+?[1-9]\d{7,14}`) e é lido como DDI dos
// EUA, derrubando o cliente sem aviso em "número diferente".
const phone = ref('+55 ');
const nameError = ref(false);
const phoneError = ref(false);
const isSubmitting = ref(false);
const isCancelling = ref(false);
const isRestarting = ref(false);
const cancelLocked = ref(false);
const confirmingCancel = ref(false);
// A tela entrou por `?inbox_id=` (retomada): não temos histórico local desse
// pareamento, então não dá pra confiar só no estado atual pra liberar o
// Cancelar sem confirmação — durante instabilidade da WAHA o backend degrada
// qualquer sessão (inclusive uma conectada há meses) pra `preparando`.
const isResuming = ref(false);
const inboxId = ref(null);
const state = ref('preparando');
const qr = ref('');
const connectedPhone = ref('');
const expectedPhone = ref('');
const preparingTooLong = ref(false);
let timer = null;
let openChatTimer = null;
let confirmExpireTimer = null;
let cancelLockTimer = null;
let preparingTimer = null;
let consecutiveFailures = 0;
// Clique em Reconectar seguido de Voltar antes da resposta chegar: sem essa
// checagem, o `finally` de `restartSession` religaria um polling que
// ninguém mais limpa (o componente já saiu de cena).
let isUnmounted = false;

const normalizedPhone = computed(() => {
  const digits = phone.value.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
});

// `conectando`: o aparelho já foi vinculado no celular, falta só o servidor
// confirmar o número — cancelar aqui derruba um pareamento que já aconteceu
// do lado do cliente, por isso exige o mesmo segundo clique de confirmação.
const needsConfirmation = computed(
  () =>
    state.value === 'numero_diferente' ||
    state.value === 'conectando' ||
    isResuming.value
);

// Mapa explícito (em vez de chave dinâmica): evita que um estado desconhecido
// vaze a chave crua de i18n na tela e mantém o lint feliz com chaves estáticas.
// Estado desconhecido cai no texto de "preparando" em vez de tela em branco.
const stateMessage = computed(() => {
  const params = {
    connected: connectedPhone.value,
    expected: expectedPhone.value,
  };
  switch (state.value) {
    case 'qr':
      return t('WHATSAPP_CONNECT.STATE.QR', params);
    case 'conectando':
      return t('WHATSAPP_CONNECT.STATE.CONECTANDO', params);
    case 'conectado':
      return t('WHATSAPP_CONNECT.STATE.CONECTADO', params);
    case 'numero_diferente':
      return t('WHATSAPP_CONNECT.STATE.NUMERO_DIFERENTE', params);
    case 'pausado':
      return t('WHATSAPP_CONNECT.STATE.PAUSADO', params);
    case 'falhou':
      return t('WHATSAPP_CONNECT.STATE.FALHOU', params);
    case 'expirado':
      return t('WHATSAPP_CONNECT.STATE.EXPIRADO', params);
    case 'sem_proxy':
      return t('WHATSAPP_CONNECT.STATE.SEM_PROXY', params);
    case 'preparando':
    default:
      return preparingTooLong.value
        ? t('WHATSAPP_CONNECT.STATE.PREPARANDO_SLOW', params)
        : t('WHATSAPP_CONNECT.STATE.PREPARANDO', params);
  }
});

// Duas causas bem diferentes levam à tela de limite: uma caixa órfã (criada
// mas nunca pareada) ocupando a vaga — `inboxId` presente, tem o que apagar
// — ou o cliente já tem mesmo um WhatsApp funcionando e está tentando um
// segundo. Um texto genérico ("fale com o suporte") confundia as duas.
const limitMessage = computed(() =>
  inboxId.value
    ? t('WHATSAPP_CONNECT.LIMIT_REACHED_ORPHAN')
    : t('WHATSAPP_CONNECT.LIMIT_REACHED')
);

// Rótulo dinâmico: o clique que arma a confirmação já deixa claro que o
// próximo é o de verdade, em vez do genérico "Cancelar" repetido duas vezes.
const cancelLabel = computed(() =>
  confirmingCancel.value
    ? t('WHATSAPP_CONNECT.ACTIONS.CONFIRM_DELETE')
    : t('WHATSAPP_CONNECT.ACTIONS.DELETE_AND_RESTART')
);

// Corre contra um timeout: se a checagem pendurar, libera quem chamou mesmo
// assim (o envio do formulário sempre revalida o limite no backend, então
// não há risco de burlar nada aqui).
const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise(resolve => {
      setTimeout(resolve, ms);
    }),
  ]);

const clearPreparingTimer = () => {
  clearTimeout(preparingTimer);
  preparingTimer = null;
  preparingTooLong.value = false;
};

const stopPolling = () => {
  clearInterval(timer);
  timer = null;
  clearTimeout(openChatTimer);
  openChatTimer = null;
  clearPreparingTimer();
};

const openChat = () =>
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: accountId.value, inbox_id: inboxId.value },
  });

const goToIndex = () => router.push({ name: 'whatsapp_index' });

const scheduleOpenChat = () => {
  openChatTimer = setTimeout(openChat, 1200);
};

const poll = async () => {
  if (document.hidden) return;

  try {
    const { data } = await WhatsappSessionsAPI.show(inboxId.value, {
      withQr: true,
    });
    consecutiveFailures = 0;
    state.value = data.state;
    expectedPhone.value = data.phone_number || expectedPhone.value;
    // O daemon omite a chave `qr` quando a busca do código falha num ciclo isolado,
    // mantendo o estado `qr`. Nesse caso conservamos o último código válido em vez
    // de apagar a imagem da tela e fazer o QR piscar.
    if (data.qr) qr.value = data.qr;
    if (data.state !== 'qr') qr.value = '';
    connectedPhone.value = data.connected_phone || '';

    // Só dispara o aviso de demora depois de PREPARING_SLOW_MS parado em
    // `preparando`; se já tinha um relógio rodando e o estado mudou, cancela.
    if (data.state === 'preparando') {
      if (!preparingTimer) {
        preparingTimer = setTimeout(() => {
          preparingTooLong.value = true;
        }, PREPARING_SLOW_MS);
      }
    } else {
      clearPreparingTimer();
    }

    if (TERMINAL_STATES.includes(data.state)) stopPolling();
    if (data.state === 'conectado') scheduleOpenChat();
  } catch (error) {
    // A caixa não pareada é apagada pelo daemon depois de 10 minutos: o
    // endpoint passa a responder 404 e isso é esperado, não uma falha transitória.
    if (error?.response?.status === 404) {
      state.value = 'expirado';
      stopPolling();
      return;
    }

    consecutiveFailures += 1;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      state.value = 'falhou';
      stopPolling();
    }
  }
};

const startPolling = () => {
  consecutiveFailures = 0;
  poll();
  timer = setInterval(poll, POLL_INTERVAL);
};

const refreshLimit = async () => {
  try {
    const { data } = await WhatsappSessionsAPI.get();
    limitReached.value = !!data?.meta?.limit_reached;
  } catch (error) {
    // Se a checagem falhar, o próprio envio do formulário continua sendo a
    // fonte da verdade (o backend recusa a criação de qualquer forma).
  }
};

const resetToForm = () => {
  stopPolling();
  step.value = 'form';
  state.value = 'preparando';
  qr.value = '';
  connectedPhone.value = '';
  expectedPhone.value = '';
  consecutiveFailures = 0;
  // `isResuming` NÃO é zerado aqui de propósito: se a tela entrou em modo
  // retomada (?inbox_id=), essa é a única memória de que não temos histórico
  // local do pareamento — ela precisa sobreviver a um "Tentar de novo" pra
  // manter a confirmação de dois cliques ativa na tela de limite (apagar
  // caixa sem pareamento). Só uma caixa NOVA de verdade zera essa flag, em
  // `submit()`, junto com o `inboxId` dela.
  confirmingCancel.value = false;
  cancelLocked.value = false;
  clearTimeout(confirmExpireTimer);
  clearTimeout(cancelLockTimer);
  // `inboxId` fica de propósito: se a caixa que expirou ainda existir no
  // Chatwell (órfã, contando pro limite de plano), a tela de limite oferece
  // apagá-la. Um novo envio do formulário sobrescreve o id normalmente.
  if (route.query.inbox_id) router.replace({ name: 'whatsapp_connect' });
  refreshLimit();
};

const addAllAgents = async () => {
  await store.dispatch('agents/get');
  const userIds = agents.value.map(agent => agent.id);
  // Toda conta tem pelo menos o administrador como agente: lista vazia aqui
  // é sinal de falha na busca (a action do store não relança), não de conta
  // sem agentes de verdade.
  if (!userIds.length) {
    useAlert(t('WHATSAPP_CONNECT.AGENTS_WARNING'));
    return;
  }

  try {
    await InboxMembersAPI.update({
      inboxId: inboxId.value,
      agentList: userIds,
    });
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECT.AGENTS_WARNING'));
  }
};

const submit = async () => {
  nameError.value = !name.value.trim();
  phoneError.value = !/^\+?[1-9]\d{7,14}$/.test(
    phone.value.replace(/[\s()-]/g, '')
  );
  if (nameError.value || phoneError.value) return;

  isSubmitting.value = true;
  try {
    await withTimeout(refreshLimit(), LIMIT_CHECK_TIMEOUT_MS);
    if (limitReached.value) {
      useAlert(t('WHATSAPP_CONNECT.LIMIT_REACHED'));
      return;
    }

    const channel = await store.dispatch('inboxes/createChannel', {
      name: name.value.trim(),
      channel: {
        type: 'api',
        additional_attributes: { phone_number: normalizedPhone.value },
      },
    });
    inboxId.value = channel.id;
    isResuming.value = false;
    expectedPhone.value = normalizedPhone.value;
    step.value = 'tracking';
    // O polling começa antes de esperar pelos agentes: uma falha ao adicionar
    // agentes não pode impedir o acompanhamento do pareamento.
    startPolling();
    addAllAgents();
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECT.STATE.FALHOU'));
  } finally {
    isSubmitting.value = false;
  }
};

const cancel = async () => {
  if (isCancelling.value || cancelLocked.value) return;

  // Apagar um número já conectado é irreversível — exige um segundo clique.
  // O primeiro só arma: trava o botão por um instante (o duplo clique não
  // passa) e a confirmação expira sozinha se ninguém confirmar.
  if (needsConfirmation.value && !confirmingCancel.value) {
    confirmingCancel.value = true;
    cancelLocked.value = true;
    clearTimeout(cancelLockTimer);
    cancelLockTimer = setTimeout(() => {
      cancelLocked.value = false;
    }, CANCEL_LOCK_MS);

    clearTimeout(confirmExpireTimer);
    confirmExpireTimer = setTimeout(() => {
      confirmingCancel.value = false;
    }, CANCEL_CONFIRM_TIMEOUT_MS);
    return;
  }

  clearTimeout(confirmExpireTimer);
  isCancelling.value = true;
  stopPolling();
  try {
    if (inboxId.value) await store.dispatch('inboxes/delete', inboxId.value);
  } catch (error) {
    // A caixa pode já não existir mais (ex.: sessão expirada) — segue para o
    // índice de qualquer forma, o botão nunca pode ficar sem efeito.
  } finally {
    router.push({ name: 'whatsapp_index' });
  }
};

const keepAnyway = () => openChat();

// Traduz a resposta do endpoint (200/409/429/503) num texto que um leigo
// entende, sem citar código HTTP. 429 é cooldown de verdade (reinício recente).
// `not_restartable` (409) é ambíguo: também dispara quando a sessão se
// recuperou sozinha entre o último refresh da tela e o clique — nesse caso
// "já tem uma reconexão em andamento" seria notícia boa com cara de erro.
// Por isso as duas usam um texto neutro: a tela se corrige no próximo ciclo
// de qualquer forma. `not_provisioned` e `session_gone` significam que não
// há mais sessão pra reiniciar. 503 é o daemon/WAHA fora do ar.
const restartErrorMessage = error => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error;
  if (status === 429 || code === 'not_restartable') {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_STATE_CHANGED');
  }
  if (code === 'not_provisioned' || code === 'session_gone') {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_NEEDS_SETUP');
  }
  if (status === 503) {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_UNAVAILABLE');
  }
  return t('WHATSAPP_CONNECT.ERRORS.RESTART_FAILED');
};

// Estamos na própria tela de acompanhamento desta caixa: sucesso não navega
// a lugar nenhum, só cutuca o polling pra pegar o QR novo o quanto antes.
// Religar o polling entra no `finally`, não só no caminho feliz: `falhou` e
// `expirado` já pararam o ciclo, então um erro aqui (ex.: 409 porque o
// estado mudou entre o clique e a resposta) não pode deixar a tela presa
// exibindo "atualizando…" pra sempre — tem que voltar a checar o estado.
const restartSession = async () => {
  if (isRestarting.value) return;
  isRestarting.value = true;
  try {
    await WhatsappSessionsAPI.restart(inboxId.value);
  } catch (error) {
    useAlert(restartErrorMessage(error));
  } finally {
    isRestarting.value = false;
    if (!isUnmounted) {
      if (!timer) {
        startPolling();
      } else {
        poll();
      }
    }
  }
};

// Só existe botão de "Tentar de novo" no estado `expirado` (a caixa não tem
// mais QR nem sessão pra reiniciar — o único caminho é recomeçar do
// formulário). `falhou`/`pausado` usam Reconectar; `sem_proxy` não tem
// botão, o daemon já tenta sozinho.
const retry = () => resetToForm();

onMounted(async () => {
  const resumeId = Number(route.query.inbox_id);
  if (resumeId) {
    inboxId.value = resumeId;
    step.value = 'tracking';
    isResuming.value = true;
    formReady.value = true;
    startPolling();
    return;
  }

  await withTimeout(refreshLimit(), LIMIT_CHECK_TIMEOUT_MS);
  formReady.value = true;
});

onUnmounted(() => {
  isUnmounted = true;
  stopPolling();
  clearTimeout(confirmExpireTimer);
  clearTimeout(cancelLockTimer);
});
</script>

<template>
  <div class="w-full h-full p-8 overflow-auto">
    <div class="max-w-md">
      <h1 class="mb-6 text-xl font-medium text-n-slate-12">
        {{ $t('WHATSAPP_CONNECT.TITLE') }}
      </h1>

      <p v-if="!formReady" class="text-sm text-n-slate-11">
        {{ $t('WHATSAPP_CONNECT.LOADING') }}
      </p>

      <template v-else>
        <div
          v-if="step === 'form' && limitReached"
          class="flex flex-col items-start gap-3"
        >
          <p class="text-sm text-n-amber-11">{{ limitMessage }}</p>
          <p v-if="confirmingCancel" class="text-sm text-n-ruby-11">
            {{ $t('WHATSAPP_CONNECT.ACTIONS.CONFIRM_CANCEL') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <NextButton
              v-if="inboxId"
              outline
              slate
              :disabled="isCancelling || cancelLocked"
              :label="$t('WHATSAPP_CONNECT.ACTIONS.DELETE_ORPHAN')"
              @click="cancel"
            />
            <NextButton
              outline
              slate
              :label="$t('WHATSAPP_CONNECT.ACTIONS.BACK_TO_INDEX')"
              @click="goToIndex"
            />
          </div>
        </div>

        <form
          v-else-if="step === 'form'"
          class="flex flex-col gap-4"
          @submit.prevent="submit"
        >
          <p class="text-sm text-n-slate-11">
            {{ $t('WHATSAPP_CONNECT.FORM.DESKTOP_HINT') }}
          </p>

          <label class="text-sm text-n-slate-12">
            {{ $t('WHATSAPP_CONNECT.FORM.NAME_LABEL') }}
            <input
              v-model="name"
              type="text"
              :placeholder="$t('WHATSAPP_CONNECT.FORM.NAME_PLACEHOLDER')"
            />
            <span v-if="nameError" class="text-sm text-n-ruby-11">
              {{ $t('WHATSAPP_CONNECT.FORM.NAME_ERROR') }}
            </span>
          </label>

          <label class="text-sm text-n-slate-12">
            {{ $t('WHATSAPP_CONNECT.FORM.PHONE_LABEL') }}
            <input
              v-model="phone"
              type="tel"
              :placeholder="$t('WHATSAPP_CONNECT.FORM.PHONE_PLACEHOLDER')"
            />
            <span v-if="phoneError" class="text-sm text-n-ruby-11">
              {{ $t('WHATSAPP_CONNECT.FORM.PHONE_ERROR') }}
            </span>
          </label>

          <NextButton
            type="submit"
            solid
            teal
            :is-loading="isSubmitting"
            :disabled="isSubmitting"
            :label="$t('WHATSAPP_CONNECT.FORM.SUBMIT')"
          />
        </form>

        <div v-else class="flex flex-col items-center gap-4 text-center">
          <p class="text-sm text-n-slate-11">{{ stateMessage }}</p>

          <p v-if="confirmingCancel" class="text-sm text-n-ruby-11">
            {{ $t('WHATSAPP_CONNECT.ACTIONS.CONFIRM_CANCEL') }}
          </p>

          <div
            v-if="state === 'qr' && qr"
            class="p-2 bg-white rounded-lg shadow outline outline-1 outline-n-strong"
          >
            <img
              :src="qr"
              :alt="$t('WHATSAPP_CONNECT.QR_ALT')"
              class="rounded size-64"
            />
          </div>

          <!-- O sistema já tenta sozinho nesse estado: um botão "Tentar de novo"
               aqui não provoca nada de novo, só confundiria. -->
          <p v-if="state === 'sem_proxy'" class="text-xs text-n-slate-10">
            {{ $t('WHATSAPP_CONNECT.STATE.SEM_PROXY_RETRYING') }}
          </p>

          <div class="flex flex-wrap justify-center gap-2">
            <NextButton
              v-if="state === 'numero_diferente'"
              solid
              teal
              :label="$t('WHATSAPP_CONNECT.ACTIONS.KEEP')"
              @click="keepAnyway"
            />
            <!-- `pausado`: número já pareado, reaproveita o mesmo botão do índice -->
            <NextButton
              v-if="state === 'pausado'"
              solid
              teal
              :label="$t('WHATSAPP_CONNECT.OPEN_CHAT')"
              @click="openChat"
            />
            <!-- `falhou`/`pausado` têm sessão de verdade pra reiniciar na WAHA;
                 `expirado` não tem (a caixa já não existe, só recomeçar do
                 formulário); `sem_proxy` não tem botão nenhum, o daemon já
                 tenta sozinho. -->
            <NextButton
              v-if="['falhou', 'pausado'].includes(state)"
              outline
              teal
              :is-loading="isRestarting"
              :disabled="isRestarting"
              :label="$t('WHATSAPP_CONNECT.ACTIONS.RECONNECT')"
              @click="restartSession"
            />
            <NextButton
              v-else-if="state === 'expirado'"
              outline
              teal
              :label="$t('WHATSAPP_CONNECT.ACTIONS.RETRY')"
              @click="retry"
            />
            <!-- Saída não-destrutiva, disponível em qualquer estado de
                 acompanhamento — sem ela o único jeito de sair era o botão
                 que apaga. -->
            <NextButton
              outline
              slate
              :label="$t('WHATSAPP_CONNECT.ACTIONS.BACK_TO_INDEX')"
              @click="goToIndex"
            />
            <!-- `pausado` (sessão pareada) e `conectado` (prestes a abrir o chat)
                 nunca mostram ação destrutiva -->
            <NextButton
              v-if="!['pausado', 'conectado'].includes(state)"
              outline
              slate
              :disabled="isCancelling || cancelLocked"
              :label="cancelLabel"
              @click="cancel"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
